import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './LifeStories.css';

/*
 * ==============================================
 * HOW TO EDIT (via Claude chat):
 * ==============================================
 * "Add a story about getting caught speeding on the NYC expressway"
 * "Add a story about my pen pal named ..."
 * "Add a near-death experience story"
 * "Add the story of how I started Chummy Chum"
 */

const storyCategories = [
  {
    key: 'incidents',
    label: 'Near-Death & Dangerous',
    icon: '⚡',
    description: 'The most dangerous incidents and close calls in my life',
    stories: [
      // === ADD DANGEROUS INCIDENT STORIES BELOW ===
      // {
      //   id: 1,
      //   title: 'The NYC Expressway',
      //   preview: 'Getting caught speeding on an NYC expressway...',
      //   content: 'Full story here...',
      //   date: 'Year',
      //   image: null,
      // },
      // === ADD DANGEROUS INCIDENT STORIES ABOVE ===
    ],
  },
  {
    key: 'friends',
    label: 'Special Friends',
    icon: '🤝',
    description: 'Friends who made a lasting impact, including my pen pal',
    stories: [
      // === ADD FRIEND STORIES BELOW ===
      // {
      //   id: 1,
      //   title: 'My Pen Pal',
      //   preview: 'How we connected across the world...',
      //   content: 'Full story here...',
      //   date: 'Year',
      //   image: null,
      // },
      // === ADD FRIEND STORIES ABOVE ===
    ],
  },
  {
    key: 'ventures',
    label: 'Ventures & Dreams',
    icon: '🚀',
    description: 'The stories behind my ventures and creations',
    stories: [
      {
        id: 1,
        title: 'How I Started Chummy Chum',
        preview: 'In September 2007, a simple but profound idea was born — a "Charity of Love" unlike any other...',
        content: 'In September 2007, together with my immediate family, I created the Chummy Chum Foundation Philippines, Inc. — born from a deep conviction that the most powerful gift you can give a child is not money or material goods, but unconditional love.\n\nWe called it the "Charity of Love" — a very unfamiliar and unique kind of charity. It\'s not the kind of institutional charity that donates funds, used clothes, or goods to organizations. This is something different. This is love that comes straight from the heart.\n\nI created Chummy Chum — a cute, funny-looking character that resembles a baby dinosaur. His purple color represents the highest level of spiritual awareness. His red and white striped t-shirt is a cheerful manifestation of his happy character and loving purpose. His apple-green toddler shorts represent a child\'s innocence and uncorrupted thoughts.\n\nUnlike other cartoon characters created for fun, Chummy Chum has directed his efforts towards God\'s special little ones — children who are unwell or have been inflicted with terminal and rare ailments. He makes his appearance several days a week at hospitals, orphanages, and children\'s centers, month after month, year after year.\n\nWhen Chummy Chum arrives, sound speakers blare out the jovial "Jump with Chummy Chum" song. A colorful popcorn cart rolls out followed by a cotton candy machine. Fresh popcorn and cotton candy are given away along with special prizes and gift items. Every child over 4 years of age gets a Chummy Chum coloring book and crayons, while toddlers get a Chummy Chum stuffed doll. Over a million of these coloring books have been distributed since we began.\n\nThe foundation is entirely self-funded by our family. We do not solicit cash donations from anyone. In June 2022, we created the first Chummy Chum storybook — a collaborative effort of our founder, trustees, staff, and a volunteer graphic artist — a 16-page book that introduces children to Chummy Chum\'s character and daily life.\n\nIn 2019, we brought our mission all the way to India. Chummy Chum India is a separate entity, registered with the Republic of India, with its own team bringing joy to little Indian boys and girls.\n\nChummy Chum will live forever and continue to do his job for generations to come. This is the promise and commitment of my wife and our three sons, who are all personally involved in the foundation.',
        date: '2007',
        image: null,
      },
      {
        id: 2,
        title: 'The Smile For Me Little One Program',
        preview: 'Our flagship initiative that runs 2-3 times a week, bringing love directly to children in hospital wards...',
        content: 'The Smile For Me Little One Program (SFMLOP) is the heart and soul of everything we do at Chummy Chum. It runs 2 to 3 times every week, bringing joy directly into hospital wards, orphanages, and children\'s centers.\n\nThe program aims to bring about a happy and emotional uplift among children, especially those stricken with terminal ailments, to make them feel loved and not alone. It always successfully brings enjoyment, amusement, and lighthearted pleasure to the children.\n\nEach visit is a special children\'s party. Chummy Chum arm bands are given to the little kids, storytelling about Chummy Chum is part of the program, and the Chummy Chum mascot dance performance is always the highlight. On some occasions, sponsor-donated food, drinks, toys, and cookies are also distributed.\n\nMany individuals have joined our mission by celebrating their own birthdays and special occasions with less fortunate children through SFMLOP. It is the Charity of Love in its purest form.\n\nBeyond the parties, we prioritize children from financially challenged families who cannot receive medical assistance. We help locate hospitals or institutions that can provide the necessary equipment, and we arrange transport or finance the required fees. We also started the Incubator Loan Program and, in March 2023, the Ventilator Loan Program — lending life-saving equipment to patients at public hospitals completely free of charge.',
        date: '2007 — Present',
        image: null,
      },
      {
        id: 3,
        title: 'Bringing Chummy Chum to India',
        preview: 'In 2019, we expanded our mission across the ocean to bring the same love to children in India...',
        content: 'In 2019, The Chummy Chum Foundation brought its charity mission all the way to the land of India. Lawfully registered with the Republic of India, Chummy Chum India is a separate independent entity with its own dedicated team.\n\nChummy Chum India carries the same mission, vision, and goal as the Philippines operation — actively implementing the Smile For Me Little One Program and doing the same beautiful work that Chummy Chum does in the Philippines.\n\nIt was a deeply meaningful moment for our family. Our roots trace back to Mumbai and the Sindh province, and bringing Chummy Chum to India felt like completing a circle — connecting our heritage with our purpose.',
        date: '2019',
        image: null,
      },
    ],
  },
  {
    key: 'memorable',
    label: 'Memorable Events',
    icon: '✨',
    description: 'Other unforgettable moments and occurrences',
    stories: [
      // === ADD MEMORABLE EVENT STORIES BELOW ===
      // === ADD MEMORABLE EVENT STORIES ABOVE ===
    ],
  },
];

function LifeStories() {
  const [activeCategory, setActiveCategory] = useState('incidents');
  const [expandedStory, setExpandedStory] = useState(null);

  const category = storyCategories.find((c) => c.key === activeCategory);

  return (
    <section className="stories section-padding" id="stories">
      <div className="container">
        <motion.div
          className="stories__header"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
        >
          <span className="section-label">Life Stories</span>
          <h2 className="section-title">
            Tales from a life<br />
            <span className="text-gradient">worth telling</span>
          </h2>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          className="stories__tabs"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {storyCategories.map((cat) => (
            <button
              key={cat.key}
              className={`stories__tab ${activeCategory === cat.key ? 'stories__tab--active' : ''}`}
              onClick={() => { setActiveCategory(cat.key); setExpandedStory(null); }}
            >
              <span className="stories__tab-icon">{cat.icon}</span>
              <span className="stories__tab-label">{cat.label}</span>
            </button>
          ))}
        </motion.div>

        {/* Stories */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            className="stories__content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <p className="stories__description">{category.description}</p>

            {category.stories.length > 0 ? (
              <div className="stories__list">
                {category.stories.map((story, i) => (
                  <motion.div
                    key={story.id}
                    className={`stories__card ${expandedStory === story.id ? 'stories__card--expanded' : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 * i }}
                    onClick={() => setExpandedStory(expandedStory === story.id ? null : story.id)}
                  >
                    <div className="stories__card-header">
                      <div>
                        <h3 className="stories__card-title">{story.title}</h3>
                        {story.date && <span className="stories__card-date">{story.date}</span>}
                      </div>
                      <span className="stories__card-toggle">
                        {expandedStory === story.id ? '−' : '+'}
                      </span>
                    </div>
                    <p className="stories__card-preview">{story.preview}</p>
                    <AnimatePresence>
                      {expandedStory === story.id && (
                        <motion.div
                          className="stories__card-full"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {story.image && (
                            <div className="stories__card-image">
                              <img src={story.image} alt={story.title} />
                            </div>
                          )}
                          <p>{story.content}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="stories__empty">
                <span className="stories__empty-icon">{category.icon}</span>
                <h3>No stories yet</h3>
                <p>Tell Claude your stories and they'll appear here!</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

export default LifeStories;
